import { Form } from 'antd5';
import styled, { css } from 'styled-components';

import { Tooltip } from 'components/ui';

type AttributeColumnProps = {
  $columnCount: number;
};

type FixedButtonContainerProps = {
  $backgroundColor?: string;
  $isImageViewer: boolean;
  $isScrolledToBottom: boolean;
};

type FormContainerProps = {
  $isScrolledToTop: boolean;
};

export const FormContainer = styled.div<FormContainerProps>`
  max-height: 100%;
  overflow-y: auto;

  ${({ $isScrolledToTop, theme }) =>
    !$isScrolledToTop &&
    `
    border-top: 1px solid ${theme.borderAccentColor};
    -webkit-box-shadow: inset 0px 5px 5px -3px ${theme.lightShadow};
    -moz-box-shadow: inset 0px 5px 5px -3px ${theme.lightShadow};
    box-shadow: inset 0px 5px 5px -3px ${theme.lightShadow};
  `}

  form {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;

    .form-fields-container {
      height: 100%;
      overflow-y: auto;
    }
  }
`;

export const StyledForm = styled(Form)`
  width: 100%;

  .ant-form-item {
    margin-bottom: 1rem;
    .ant-form-item-control {
      text-align: start;
    }
    .ant-form-item-control-input-content {
      display: flex;
      flex-direction: column;
      text-align: start;

      &:has(.ant-switch) {
        display: block;
      }
    }
  }
`;

export const HintText = styled.span`
  color: ${({ theme }) => theme.gray};
`;

export const ComputedMetricInput = styled.div`
  width: 100%;
`;

export const RevertComputedValueButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  margin-left: 4px;
  padding: 2px;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.teal};
  cursor: pointer;
`;

export const AttributeFields = styled.div<AttributeColumnProps>`
  ${({ $columnCount }) =>
    $columnCount > 1
      ? `
        display: grid;
        grid-template-columns: repeat(${$columnCount}, minmax(0, 1fr));
        column-gap: 3rem;
        align-items: stretch;

        @media (max-width: 640px) {
          grid-template-columns: minmax(0, 1fr);
        }
      `
      : `
        display: contents;
      `}
`;

export const AttributeColumn = styled.div<AttributeColumnProps>`
  ${({ $columnCount, theme }) =>
    $columnCount > 1
      ? `
        min-width: 0;
        padding: 12px 0;
        position: relative;

        &:not(:first-child)::before {
          content: '';
          position: absolute;
          top: 0;
          bottom: 0;
          left: -1.5rem;
          border-left: 1px solid ${theme.borderColor};
        }

        @media (max-width: 640px) {
          padding: 0;

          &:first-child {
            padding-top: 12px;
          }

          &:last-child {
            padding-bottom: 12px;
          }

          &:not(:first-child)::before {
            content: none;
          }
        }
      `
      : `
        display: contents;

        .ant-form-horizontal & > .ant-form-item:first-of-type {
          margin-top: 12px;
        }
      `}
`;

export const FixedButtonContainer = styled.div<FixedButtonContainerProps>`
  background-color: ${({ $backgroundColor, theme }) =>
    $backgroundColor ?? theme.secondaryBackgroundColor};
  border-top: 1px solid ${({ theme }) => theme.lightShadow};
  padding: ${({ $isImageViewer }) => ($isImageViewer ? '12px' : '12px 12px 0 12px')};

  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;

  ${({ $isScrolledToBottom, theme }) =>
    !$isScrolledToBottom &&
    `
    -webkit-box-shadow: 0px -5px 5px -3px ${theme.lightShadow};
    -moz-box-shadow: 0px -5px 5px -3px ${theme.lightShadow};
    box-shadow: 0px -5px 5px -3px ${theme.lightShadow};
  `}
`;

export const LabelActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  width: 16px;
  height: 16px;
  margin-left: 4px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.teal};
  cursor: pointer;
  font-size: 12px;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

export const HelpTooltip = styled(Tooltip)`
  margin-right: 0.2rem;
`;

/**
 * Tightened layout for `AttributesForm` rendered with `formLayout="horizontal"`: the label sits in
 * a fixed left column with the control filling the rest, instead of antd's even 50/50 split.
 *
 * Opt-in per surface rather than applied inside `AttributesForm` itself, so other horizontal
 * consumers (`DamageAttributes`) keep the default antd sizing. Interpolate it into the styled
 * container that wraps the form.
 */
export const horizontalAttributesFormStyles = css`
  .ant-form-horizontal .ant-form-item-label {
    flex: 0 0 8rem;
    max-width: 8rem;
    text-align: left;
    padding-right: 8px;
  }

  .ant-form-horizontal .ant-form-item-control {
    flex: 1 1 0;
    min-width: 0;
    max-width: none;
  }

  .ant-form-horizontal .ant-form-item-label > label {
    height: auto;
    min-height: 32px;
    white-space: normal;
  }

  .ant-form-horizontal .ant-form-item-label > label::after {
    content: none;
  }
`;
